import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";
import { FEATURES } from "@/config/features";

export interface AutomationTimeSavingsData {
  totalHoursPerYear: number;
  perGuideEstimates: Array<{ sopTitle: string; hoursPerYear: number }>;
  guideCount: number;
}

export interface DashboardStats {
  sopsTotal: number;
  sopsApproved: number;
  sopsDraft: number;
  automationGuides: number;
}

export interface DashboardSOP {
  id: string;
  title: string;
  status: string | null;
  updated_at: string;
  approved_at: string | null;
}

export interface DashboardProcessLog {
  id: string;
  name: string;
  status: string | null;
  updated_at: string;
}

export interface DashboardAutomationGuide {
  id: string;
  title: string;
  created_at: string;
}

export interface DashboardData {
  stats: DashboardStats;
  automationTimeSavings: AutomationTimeSavingsData | null;
  recentSOPs: DashboardSOP[];
  draftSOPs: DashboardSOP[];
  draftProcessLogs: DashboardProcessLog[];
  recentAutomationGuides: DashboardAutomationGuide[];
}

async function fetchAutomationTimeSavings(
  organizationId: string,
): Promise<AutomationTimeSavingsData | null> {
  const { data, error } = await supabase
    .from("automation_guides")
    .select("estimated_time_savings_per_year")
    .eq("organization_id", organizationId)
    .not("estimated_time_savings_per_year", "is", null);

  if (error) {
    console.error("Error fetching automation time savings:", error);
    throw new Error(`Failed to load time savings: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return null;
  }

  const totalHours = data.reduce(
    (sum, item) => sum + Number(item.estimated_time_savings_per_year || 0),
    0,
  );

  return {
    totalHoursPerYear: Math.round(totalHours),
    perGuideEstimates: [],
    guideCount: data.length,
  };
}

async function fetchDashboardData(
  organizationId: string,
): Promise<DashboardData> {
  // Build the list of queries based on feature flags
  const queries: Promise<any>[] = [];

  // SOP queries - only fetch if SOP_LIBRARY is enabled
  if (FEATURES.SOP_LIBRARY) {
    queries.push(
      supabase
        .from("sops")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organizationId),
      supabase
        .from("sops")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("status", "approved"),
      supabase
        .from("sops")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("status", "draft"),
    );
  } else {
    // Return empty results for SOP queries
    queries.push(
      Promise.resolve({ count: 0 }),
      Promise.resolve({ count: 0 }),
      Promise.resolve({ count: 0 }),
    );
  }

  // Automation guides count - always fetch
  queries.push(
    supabase
      .from("automation_guides")
      .select("id", { count: "exact", head: true })
      .eq("organization_id", organizationId),
  );

  // Recent and draft SOPs - only fetch if SOP_LIBRARY is enabled
  if (FEATURES.SOP_LIBRARY) {
    queries.push(
      supabase
        .from("sops")
        .select("id, title, status, updated_at, approved_at")
        .eq("organization_id", organizationId)
        .eq("status", "approved")
        .order("approved_at", { ascending: false })
        .limit(5),
      supabase
        .from("sops")
        .select("id, title, status, updated_at")
        .eq("organization_id", organizationId)
        .eq("status", "draft")
        .order("updated_at", { ascending: false })
        .limit(5),
    );
  } else {
    // Return empty results for SOP list queries
    queries.push(Promise.resolve({ data: [] }), Promise.resolve({ data: [] }));
  }

  // Draft process logs without automation guides - always fetch
  // Fetch both datasets in parallel and filter client-side to avoid serial query delay.
  queries.push(
    Promise.all([
      supabase
        .from("automation_guides")
        .select("process_log_id")
        .eq("organization_id", organizationId)
        .not("process_log_id", "is", null),
      supabase
        .from("process_logs")
        .select("id, name, status, updated_at")
        .eq("organization_id", organizationId)
        .eq("status", "draft")
        .order("updated_at", { ascending: false })
        .limit(25),
    ]).then(([guidesResult, logsResult]) => {
      if (logsResult.error) {
        return { data: [], error: logsResult.error };
      }

      const guideProcessLogIds = new Set(
        (guidesResult.data ?? [])
          .map((row) => row.process_log_id)
          .filter((id): id is string => Boolean(id)),
      );

      const filteredDrafts = (logsResult.data ?? []).filter(
        (log) => !guideProcessLogIds.has(log.id),
      );

      return {
        data: filteredDrafts,
        error: guidesResult.error ?? null,
      };
    }),
  );

  // Recent automation guides - always fetch
  queries.push(
    supabase
      .from("automation_guides")
      .select(
        `
        id,
        created_at,
        sops(title),
        process_logs(name)
      `,
      )
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false })
      .limit(5),
  );

  // Automation time savings - always fetch
  queries.push(fetchAutomationTimeSavings(organizationId));

  const [
    sopsResult,
    sopsApprovedResult,
    sopsDraftResult,
    automationGuidesResult,
    recentSopsResult,
    draftSopsResult,
    draftProcessLogsResult,
    recentAutomationGuidesResult,
    automationTimeSavingsData,
  ] = await Promise.all(queries);

  // Check for critical errors that should fail the entire query
  const criticalErrors: string[] = [];

  if (automationGuidesResult.error) {
    criticalErrors.push(
      `Automation guides: ${automationGuidesResult.error.message}`,
    );
  }
  if (draftProcessLogsResult.error) {
    criticalErrors.push(
      `Process logs: ${draftProcessLogsResult.error.message}`,
    );
  }
  if (recentAutomationGuidesResult.error) {
    criticalErrors.push(
      `Recent guides: ${recentAutomationGuidesResult.error.message}`,
    );
  }

  // Log non-critical SOP errors but don't fail (SOP feature may be disabled)
  if (FEATURES.SOP_LIBRARY) {
    if (sopsResult.error) console.warn("SOP count error:", sopsResult.error);
    if (sopsApprovedResult.error)
      console.warn("SOP approved count error:", sopsApprovedResult.error);
    if (sopsDraftResult.error)
      console.warn("SOP draft count error:", sopsDraftResult.error);
    if (recentSopsResult.error)
      console.warn("Recent SOPs error:", recentSopsResult.error);
    if (draftSopsResult.error)
      console.warn("Draft SOPs error:", draftSopsResult.error);
  }

  // Throw if any critical errors occurred
  if (criticalErrors.length > 0) {
    throw new Error(`Dashboard load failed: ${criticalErrors.join("; ")}`);
  }

  // Transform automation guides to include title from linked SOP or process_log
  const recentAutomationGuides: DashboardAutomationGuide[] = (
    recentAutomationGuidesResult.data || []
  ).map((guide: any) => ({
    id: guide.id,
    title: guide.sops?.title || guide.process_logs?.name || "Untitled",
    created_at: guide.created_at,
  }));

  return {
    stats: {
      sopsTotal: sopsResult.count || 0,
      sopsApproved: sopsApprovedResult.count || 0,
      sopsDraft: sopsDraftResult.count || 0,
      automationGuides: automationGuidesResult.count || 0,
    },
    automationTimeSavings: automationTimeSavingsData,
    recentSOPs: recentSopsResult.data || [],
    draftSOPs: draftSopsResult.data || [],
    draftProcessLogs: draftProcessLogsResult.data || [],
    recentAutomationGuides,
  };
}

interface UseDashboardDataOptions {
  enabled?: boolean;
}

/**
 * Shared hook for fetching dashboard data with TanStack Query caching.
 * Data is cached for 5 minutes and shared across all components.
 *
 * Usage:
 * - In Dashboard: const { data, isLoading, refetch } = useDashboardData();
 * - When SOPs change: invalidateSOPs() already invalidates ['dashboard']
 * - For manual refresh: call refetch() or useInvalidateDashboard()
 */
export function useDashboardData(options: UseDashboardDataOptions = {}) {
  const { organization } = useUserProfile();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ["dashboard", orgId],
    queryFn: () => fetchDashboardData(orgId!),
    enabled: (options.enabled ?? true) && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get the invalidation function for dashboard cache.
 * Call this after actions that affect dashboard data.
 */
export function useInvalidateDashboard() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };
}

/**
 * Utility to invalidate dashboard cache from outside React components.
 * Use sparingly - prefer useInvalidateDashboard hook when possible.
 */
export function invalidateDashboardCache(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
}
